import React, { ReactElement, useContext } from 'react'
import { useNavigate, useParams, NavigateFunction } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faAngleRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { marked } from 'marked'
import dompurify from 'dompurify'
import { AppContext } from '../contexts/AppContext'
import { isLoggedIn } from './Login'
import { Dispatch, PrimaryAction } from '../helpers/action/Action.d'
import { AppContextState } from '../contexts/AppContext.d'
library.add(faAngleRight)

import AboutMarkdown from './about/about'
import PrivacyMarkdown from './about/privacy'
import ErrorMessage from '../common/ErrorMessage'
import { ExploreHelp } from '../Explore/ExploreHelp'

const markdowns: Record<string, string> = {
    about: AboutMarkdown,
    privacy: PrivacyMarkdown
}

/* eslint-disable dot-notation, @typescript-eslint/naming-convention */

const DEFAULT_PAGE = 'about'

export const About = (): ReactElement => {
    const { appState } = useContext(AppContext)
    return !isLoggedIn(appState)
        ? <div className="app"><AboutContent /></div>
        : <AboutContent />
}

const AboutContent = (): ReactElement => {
    const params = useParams<{ page?: string }>()
    const page = params.page || DEFAULT_PAGE
    return <>
        <Breadcrumbs page={ page } />
        <AboutPage key={ page } page={ page } />
    </>
}

const Breadcrumbs = (props: { page: string }): ReactElement =>
    <div data-testid="breadcrumbs" id="breadcrumbs">
        <Link to="/"><img src="/images/logo.svg" alt="Trove" /></Link>
        <FontAwesomeIcon icon="angle-right" /><Link to={ `/${DEFAULT_PAGE}/` }>About</Link>
        { props.page !== DEFAULT_PAGE && <>
            <FontAwesomeIcon icon="angle-right" />
            <Link to={ `/${DEFAULT_PAGE}/${props.page}` }>{props.page.substring(0, 1).toUpperCase() + props.page.substring(1)}</Link>
        </>}
    </div>

const linkTarget = (href: string): string => href.replace(/.*\/about/, '/about')

/** Hackily take control of Markdown links, to avoid having to reload the page each time. */
const linkListener = (
    appState: AppContextState, appDispatch: Dispatch<PrimaryAction>, navigate: NavigateFunction
): React.MouseEventHandler<HTMLDivElement> =>
    (e) => {
        if (e.target['tagName'] === 'A') {
            if (e.target['href'].startsWith('mailto:')) {
                return
            } else if (e.target['href'].includes('/about/account/')) {
                const user = linkTarget(String(e.target['href'])).substring(15)
                e.preventDefault()
                if (isLoggedIn(appState)) {
                    e.target['classList'].toggle('disabled')
                } else {
                    void navigate('/write?date=1970-01-01')
                    appDispatch({ type: 'SKIP_AUTH_TO_USER', user })
                }
                return
            }
            void navigate(linkTarget(String(e.target['href'])))
            e.preventDefault()
        }
    }

const AboutPage = (props: { page: string }): ReactElement => {
    const navigate = useNavigate()
    const { appState, appDispatch } = React.useContext(AppContext)
    if (props.page === 'explore') {
        return <ExploreHelp onExplorePage={ false } />
    }
    if (!(props.page in markdowns))
        return <ErrorMessage status={ 404 } />
    return <div
        className="about-page"
        role="article"
        onClick={ linkListener(appState, appDispatch, navigate) }
        dangerouslySetInnerHTML={ markdownHtml(markdowns[props.page]) } />
}

const markdownHtml = (markdown: string): { __html: string } =>
    ({ __html: dompurify.sanitize(marked(markdown, { async: false })) })
